import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, of, switchMap } from 'rxjs';
import { promiseWithCatch } from 'src/app/common/app.helpers';
import ProjectLinks from 'src/app/common/project.links';
import { FullUser, UserRole } from 'src/app/swagger-api';
import { SessionService } from '../../../auth/session.service';
import { Session } from '../../../interfaces';

@Injectable()
export abstract class RoleLevelGuard {
  public sessionService = inject(SessionService);
  public router = inject(Router);

  public canActivate(_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<boolean> {
    return this.checkGuard();
  }

  public canActivateChild(_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<boolean> {
    return this.checkGuard();
  }

  public checkGuard(): Observable<boolean> {
    const isLoggedIn: boolean = this.sessionService.isSessionActive();

    if (!isLoggedIn) {
      promiseWithCatch(this.router.navigateByUrl(ProjectLinks.LINK_DEFAULT));
      return of(false);
    }

    const user = this.sessionService.user;

    if (!user) {
      return this.sessionService.sessionChanges.pipe(
        switchMap((session: Session) => {
          if (!session.user) {
            promiseWithCatch(this.router.navigateByUrl(ProjectLinks.LINK_DEFAULT));
            return of(false);
          }

          return this.checkUserGuard(session.user);
        }),
      );
    } else {
      return this.checkUserGuard(user);
    }
  }

  protected abstract getRole(): UserRole;

  private checkUserGuard(user: FullUser): Observable<boolean> {
    const userRoles = user.user_roles;

    if (!userRoles.includes(this.getRole())) {
      promiseWithCatch(this.router.navigateByUrl(ProjectLinks.LINK_DEFAULT));
      return of(false);
    }

    return of(true);
  }
}

@Injectable()
export class DoctorRoleGuard extends RoleLevelGuard {
  protected getRole(): UserRole {
    return UserRole.Doctor;
  }
}

@Injectable()
export class PatientRoleGuard extends RoleLevelGuard {
  protected getRole(): UserRole {
    return UserRole.Patient;
  }
}
